import {
  catchError,
  from,
  map,
  mergeMap,
  Observable,
  of,
  timeout,
  TimeoutError,
  toArray,
} from "rxjs";
import { EnrichmentService } from "@app/shared/services/enrichment.service";
import { ENRICHMENT_HEADER_TRANSPORT } from "../../../services/enrichment-data.service";
import {
  LIFECYCLE_ACTIONS,
  LifecycleAction,
  scheduleDirection,
  TransitionPlan,
} from "./price-transitions";
import { refusedForRoleSwitch, SCOPE_CHANGED, WriteScope } from "./write-scope";

/** What one package's request did. Each request is all-or-nothing, so every
 *  price in it shares the outcome. */
export interface TransitionOutcome {
  offeringId: string;
  priceIds: readonly string[];
  ok: boolean;
  /** Why it failed, worded for the user. */
  message?: string;
}

const SEND_CONCURRENCY = 4;
/** HttpClient never gives up on its own, and the list holds every lifecycle
 *  action until the run ends. */
const REQUEST_TIMEOUT_MS = 60_000;

/** Send `plan`: one request per package, and one outcome per package in plan
 *  order. A failed request becomes a failed outcome, never an error, so the
 *  rest still go out. `scheduledAt` is the RFC 3339 time for a schedule. */
export function sendTransitions(
  enrichmentService: EnrichmentService,
  plan: TransitionPlan,
  action: LifecycleAction,
  scheduledAt: string,
  scope: WriteScope,
): Observable<TransitionOutcome[]> {
  const order = [...plan.requests.keys()];
  return from([...plan.requests]).pipe(
    mergeMap(([offeringId, priceIds]) => {
      if (!scope.holds()) {
        return of<TransitionOutcome>({
          offeringId,
          priceIds,
          ok: false,
          message: SCOPE_CHANGED,
        });
      }
      return request(
        enrichmentService,
        action,
        offeringId,
        [...priceIds],
        scheduledAt,
        scope.channel,
      ).pipe(
        timeout(REQUEST_TIMEOUT_MS),
        map((): TransitionOutcome => ({ offeringId, priceIds, ok: true })),
        catchError((error: unknown) =>
          of<TransitionOutcome>({
            offeringId,
            priceIds,
            ok: false,
            message: failureMessage(action, error),
          }),
        ),
      );
    }, SEND_CONCURRENCY),
    toArray(),
    map((outcomes) =>
      outcomes.sort(
        (a, b) => order.indexOf(a.offeringId) - order.indexOf(b.offeringId),
      ),
    ),
  );
}

function request(
  enrichmentService: EnrichmentService,
  action: LifecycleAction,
  offeringId: string,
  priceIds: string[],
  scheduledAt: string,
  channel: string,
): Observable<unknown> {
  const options = { ...ENRICHMENT_HEADER_TRANSPORT, channel };
  switch (action) {
    case "publish":
      return enrichmentService.publishEnrichment(offeringId, options, priceIds);
    case "reject":
      return enrichmentService.rejectEnrichment(offeringId, options, priceIds);
    case "schedulePublish":
      return enrichmentService.schedulePublish(
        offeringId,
        scheduledAt,
        priceIds,
        options,
      );
    case "scheduleUnpublish":
      return enrichmentService.scheduleUnpublish(
        offeringId,
        scheduledAt,
        priceIds,
        options,
      );
  }
}

export function outcomeSummary(
  action: LifecycleAction,
  outcomes: readonly TransitionOutcome[],
  channelLabel = "",
): string {
  const prices = (ok: boolean): number =>
    outcomes
      .filter((o) => o.ok === ok)
      .reduce((n, o) => n + o.priceIds.length, 0);
  const done = prices(true);
  const failed = prices(false);
  const total = done + failed;
  const where = channelLabel ? ` on ${channelLabel}` : "";
  const line = `${done} of ${total} ${total === 1 ? "price" : "prices"} ${LIFECYCLE_ACTIONS[action].done}${where}.`;
  return failed ? `${line} ${failed} could not be changed.` : line;
}

/** The user-facing reason a package's request failed. A 409 is worded so it
 *  never points at a cancel the list cannot show: the open schedule may sit
 *  under another package that shares the price. */
function failureMessage(action: LifecycleAction, error: unknown): string {
  if (refusedForRoleSwitch(error)) return SCOPE_CHANGED;
  const status = (error as { status?: number } | null)?.status;
  if (error instanceof TimeoutError) {
    return "No answer from the server. The change may still have been made.";
  }
  const detail = (error as { error?: { desc?: string } } | null)?.error?.desc;
  const direction = scheduleDirection(action);
  switch (status) {
    case 400: {
      const base = direction
        ? "Refused: the time must be in the future, and every price must still be eligible."
        : `Refused: a price is no longer ${LIFECYCLE_ACTIONS[action].requires} on this channel.`;
      return detail ? `${base} (${detail})` : base;
    }
    case 403:
      return "You do not have permission for this action.";
    case 404:
      return "This product offering has no enrichment on this channel.";
    case 409:
      return `A price already has a ${direction === "unpublish" ? "take-down" : "publish"} scheduled, possibly under another product offering that shares it.`;
    default:
      return "The request failed. Please try again.";
  }
}
